import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ArrowRight, BookOpen, CheckCircle2, Search } from "lucide-react";

import { API_BASE_URL } from "../../../../api";
import { fetchLearnerModulesRequest } from "../../../redux/actions/learnerModulesActions";
import "./MesModulesPage.css";

const getModuleImageUrl = (picture) =>
    picture ? `${API_BASE_URL}/api/modules-cle/images/${picture}` : null;

const normalize = (value) => String(value || "").trim().toLowerCase();

const getStatusMeta = (status) => {
    const normalized = String(status || "NOT_STARTED").toUpperCase();
    if (normalized === "COMPLETED") return { label: "Termine", className: "lm-status--completed" };
    if (normalized === "IN_PROGRESS") return { label: "En cours", className: "lm-status--progress" };
    if (normalized === "FAILED") return { label: "A reprendre", className: "lm-status--failed" };
    return { label: "Non commence", className: "lm-status--neutral" };
};

export default function MesModulesPage() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { items = [], loading = false, error = null } = useSelector((state) => state.learnerModules || {});
    const [search, setSearch] = useState("");

    useEffect(() => {
        dispatch(fetchLearnerModulesRequest());
    }, [dispatch]);

    const filteredItems = useMemo(() => {
        const query = normalize(search);
        return (items || [])
            .filter((item) => item.type === "FORMATION")  // ← ajouter cette ligne
            .filter((item) => (
                !query
                || normalize(item.nom).includes(query)
                || normalize(item.reference).includes(query)
                || normalize(item.formationNom).includes(query)
                || normalize(item.campagneNom).includes(query)
            ));
    }, [items, search]);

    return (
        <main className="lm-page">
            <header className="lm-hero">
                <h1>Mes parcours</h1>
            </header>

            <section className="lm-toolbar">
                <div className="lm-search">
                    <Search size={16} />
                    <input
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Rechercher par parcours, formation ou campagne"
                    />
                </div>
                <div className="lm-toolbar__meta">
                    {loading ? "Chargement..." : `${filteredItems.length} parcours`}
                </div>
            </section>

            {error ? <section className="lm-state lm-state--error">{error}</section> : null}

            {!loading && filteredItems.length === 0 ? (
                <section className="lm-state">
                    <BookOpen size={18} />
                    Aucun parcours affecté pour le moment.
                </section>
            ) : (
                <section className="lm-grid">
                    {filteredItems.map((moduleItem) => {
                        const progress = moduleItem.progress || {};
                        const statusMeta = getStatusMeta(progress.status);
                        const progressPercent = progress.progressPercent || 0;

                        return (
                            <article
                                className="lm-card"
                                key={moduleItem.id}
                                onClick={() => navigate(`/formation/apprenant/modules/${moduleItem.id}/player`)}
                            >
                                <div className="lm-card__media">
                                    {moduleItem.picture ? (
                                        <img src={getModuleImageUrl(moduleItem.picture)} alt={moduleItem.nom} />
                                    ) : (
                                        <div className="lm-card__placeholder">
                                            <BookOpen size={28} />
                                        </div>
                                    )}
                                    <div className={`lm-card__status-badge ${statusMeta.className}`}>
                                        {statusMeta.label}
                                    </div>
                                </div>

                                <div className="lm-card__body">
                                    <h2 className="lm-card__title" title={moduleItem.nom}>{moduleItem.nom}</h2>

                                    <div className="lm-card__info">
                                        <span className="lm-card__formation">{moduleItem.formationNom || "Formation"}</span>
                                        {moduleItem.campagneNom ? <span className="lm-card__campagne">{moduleItem.campagneNom}</span> : null}
                                    </div>

                                    <div className="lm-card__stats">
                                        <span>{moduleItem.totalSectionsCount || 0} sections</span>
                                        <span className="lm-card__stats-separator" />
                                        <span>{moduleItem.totalContentsCount || 0} contenus</span>
                                    </div>

                                    <div className="lm-card__progress-container">
                                        <div className="lm-card__progress-bar">
                                            <div className="lm-card__progress-fill" style={{ width: `${progressPercent}%` }} />
                                        </div>
                                        <div className="lm-card__progress-text">
                                            <span>{progressPercent}% terminé</span>
                                            {progress.status === "COMPLETED" ? <CheckCircle2 size={14} className="lm-card__completed-icon" /> : null}
                                        </div>
                                    </div>

                                    <div className="lm-card__footer">
                                        <button
                                            type="button"
                                            className="lm-card__action"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                navigate(`/formation/apprenant/modules/${moduleItem.id}/player`);
                                            }}
                                        >
                                            {progressPercent > 0 ? "Continuer" : "Commencer"}
                                            <ArrowRight size={16} />
                                        </button>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </section>
            )}
        </main>
    );
}
